import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { referralPortalClient, ReferralPortalAdminDashboard, ReferralPortalRequestRow } from "@/services/referralPortalClient";

import "./referralPortal.css";

function formatDate(value: string | null) {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function ReferralPortalDashboardPage() {
  const navigate = useNavigate();
  const session = referralPortalClient.getSession();
  const [rows, setRows] = useState<ReferralPortalRequestRow[]>([]);
  const [adminDashboard, setAdminDashboard] = useState<ReferralPortalAdminDashboard | null>(null);
  const [status, setStatus] = useState("Loading your requests...");
  const [statusTone, setStatusTone] = useState<"info" | "success" | "warning" | "danger">("info");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        if (session?.isAdmin) {
          const data = await referralPortalClient.getAdminDashboard();
          if (cancelled) {
            return;
          }
          setAdminDashboard(data);
          setRows(data.rows);
          setStatusTone("info");
          setStatus(data.rows.length === 0 ? "No referral requests submitted yet." : `${data.rows.length} referral request(s) loaded.`);
          return;
        }

        const data = await referralPortalClient.listRequests();
        if (cancelled) {
          return;
        }
        setAdminDashboard(null);
        setRows(data);
        setStatusTone("info");
        setStatus(data.length === 0 ? "No requests submitted yet." : `${data.length} request(s) loaded.`);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setStatusTone("danger");
        setStatus((error as Error).message);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="referral-portal">
      <div className="referral-portal__shell">
        <div className="referral-portal__layout">
          <section className="referral-portal__panel">
            <div className="referral-portal__eyebrow">{session?.isAdmin ? "Referral Admin" : "My Requests"}</div>
            <h1>{session?.isAdmin ? "Referral program dashboard" : session?.clientName || session?.email || "Portal dashboard"}</h1>
            <p>
              {session?.isAdmin
                ? "This admin view stays inside the same referral portal style while giving you a live overview of referral intake across the shared PMapp database."
                : "This dashboard is fed by the new referral portal tables in the shared PMapp database. As requests are reviewed and linked, quotation and project references will appear here."}
            </p>
            <div className="referral-portal__panel-actions">
              {session?.isAdmin ? null : (
                <button className="referral-portal__button" onClick={() => navigate("/refer/request-quote")}>
                  Submit another request
                </button>
              )}
              <button
                className="referral-portal__button--ghost"
                onClick={() => {
                  referralPortalClient.logout();
                  navigate("/refer/login");
                }}
              >
                Log out
              </button>
            </div>
            {session?.isAdmin && adminDashboard ? (
              <div className="referral-portal__grid">
                <div className="referral-portal__feature">
                  <strong>Total requests</strong>
                  <span className="referral-portal__metric">{adminDashboard.summary.totalRequests}</span>
                </div>
                <div className="referral-portal__feature">
                  <strong>Pending review</strong>
                  <span className="referral-portal__metric">{adminDashboard.summary.pendingReviewCount}</span>
                </div>
                <div className="referral-portal__feature">
                  <strong>Duplicate review</strong>
                  <span className="referral-portal__metric">{adminDashboard.summary.duplicateReviewCount}</span>
                </div>
                <div className="referral-portal__feature">
                  <strong>Quoted / Projects</strong>
                  <span className="referral-portal__metric">{adminDashboard.summary.quotedCount} / {adminDashboard.summary.projectCount}</span>
                </div>
              </div>
            ) : null}
          </section>

          <aside className="referral-portal__card">
            <div className={`referral-portal__status referral-portal__status--${statusTone}`}>{status}</div>
            <div className="referral-portal__upload-list" style={{ marginTop: 16 }}>
              {rows.map((row) => (
                <div key={row.referralLeadId} className="referral-portal__request-row">
                  <div className="referral-portal__request-topline">
                    <strong>{row.requestCode || `Request #${row.referralLeadId}`}</strong>
                    <span>{formatDate(row.submittedDate)}</span>
                  </div>
                  {session?.isAdmin ? (
                    <div className="referral-portal__muted">
                      Referral: {row.referralName || row.referralEmail || row.portalUserEmail || "-"}
                      {row.referralPhone ? ` • ${row.referralPhone}` : ""}
                    </div>
                  ) : null}
                  <div>{row.projectAddress || "-"}</div>
                  <div className="referral-portal__request-meta">
                    <span className="referral-portal__badge">{row.reviewStatus || row.referralStatus || "PendingReview"}</span>
                    {row.customerType ? <span>Role: {row.customerType}</span> : null}
                    {row.helpType ? <span>Need: {row.helpType}</span> : null}
                    <span>Client: {row.clientName || row.clientEmail || "-"}</span>
                    <span>Quotation: {row.quotationNo || "-"}</span>
                    <span>Project: {row.projectNo || "-"}</span>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
